import React, { useState } from 'react';

/**
 * ISO27001RequirementsMatrix component displaying a matrix of ISO27001 requirements
 * @returns {JSX.Element}
 */
function ISO27001RequirementsMatrix() {
  // State to track expanded sections
  const [expandedSections, setExpandedSections] = useState({
    '4': true,
    '5': true,
    '6': true,
    '7': false,
    '8': false,
    '9': false,
    '10': false
  });

  // Toggle section expansion
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // ISO27001 requirements data
  const requirementsData = [
    {
      section: '4. Context of the Organization',
      subsections: [
        { 
          id: '4.1', 
          title: 'Understanding the organization and its context', 
          requirement: 'Determine internal and external issues relevant to information security objectives', 
          documents: [
            'Required Document(s):',
            '- 01-ISMS Scope Document'
          ], 
          responsibility: 'Client' 
        },
        { 
          id: '4.2', 
          title: 'Understanding the needs and expectations of interested parties', 
          requirement: 'Identify relevant interested parties and their requirements', 
          documents: [
            'Required Document(s):',
            '- 01-ISMS Scope Document',
            '- Interested Parties Log'
          ], 
          responsibility: 'Client' 
        },
        { 
          id: '4.3', 
          title: 'Determining the scope of the ISMS', 
          requirement: 'Define boundaries and applicability of the ISMS', 
          documents: [
            'Required Document(s):',
            '- 01-ISMS Scope Document'
          ], 
          responsibility: 'Client' 
        },
        { 
          id: '4.4', 
          title: 'Information security management system', 
          requirement: 'Establish, implement, maintain and improve the ISMS', 
          documents: [
            'Required Document(s):',
            '- 02-ISMS Policy',
            '- ISMS Governance Council Meeting Minutes'
          ], 
          responsibility: 'Client' 
        }
      ]
    },
    {
      section: '5. Leadership',
      subsections: [
        { 
          id: '5.1', 
          title: 'Leadership and commitment', 
          requirement: 'Demonstrate leadership commitment to the ISMS', 
          documents: [
            'Kolega Responsibility:',
            '- ISMS Governance Structure',
            '- ISMS Technical Leadership Meeting Minutes',
            '- Technical Security Roadmap',
            '- Resource Allocation Documentation',
            '',
            'Client Responsibility:',
            '- ISMS Governance Council Meeting Minutes',
            '- Management Communication Plan',
            '- ISMS Implementation Plan',
            '- Leadership Training Records'
          ], 
          responsibility: 'Shared' 
        },
        { 
          id: '5.2', 
          title: 'Policy', 
          requirement: 'Establish information security policy and objectives', 
          documents: [
            'Kolega Responsibility:',
            '- 02-ISMS Information Security Management System (ISMS) Policy',
            '- Access Control Policy',
            '- Asset Management Policy',
            '- Business Continuity and Disaster Recovery Plan',
            '- Cryptography Policy',
            '- Data Management Policy',
            '- Human Resource Security Policy',
            '- Incident Response Plan',
            '- Information Security Policy (AUP)',
            '- Information Security Roles and Responsibilities',
            '- Operations Security Policy',
            '- Physical Security Policy',
            '- Risk Management Policy',
            '- Secure Development Policy',
            '- Third-Party Management Policy',
            '- Code of Conduct',
            '- 10-ISMS Information Security Objectives Plan',
            '',
            'Client Responsibility:',
            '- Organizational versions of all the above policies that reference and integrate with Kolega\'s technical policies'
          ], 
          responsibility: 'Shared' 
        },
        { 
          id: '5.3', 
          title: 'Organizational roles, responsibilities and authorities', 
          requirement: 'Assign information security responsibilities and authorities', 
          documents: [
            'Kolega Responsibility:',
            '- 03-ISMS Roles, Responsibilities, and Authorities',
            '- Technical Security Reporting Structure',
            '- Security Committee Charter',
            '- Security Performance Reporting Templates',
            '- Job Descriptions',
            '- Delegation of Authority Documentation',
            '',
            'Client Responsibility:',
            '- 03-ISMS Roles, Responsibilities, and Authorities',
            '- Organizational Security Reporting Structure',
            '- ISMS Performance Reporting Process',
            '- Job Descriptions'
          ], 
          responsibility: 'Shared' 
        }
      ]
    },
    {
      section: '6. Planning',
      subsections: [
        { 
          id: '6.1.1a', 
          title: 'General actions to address risks and opportunities', 
          requirement: 'Consider organizational context and requirements to determine risks and opportunities', 
          documents: [
            'Required Document(s):',
            '- 04-ISMS Risk Assessment and Risk Treatment Process',
            '- Risk Register'
          ], 
          responsibility: 'Client' 
        },
        { 
          id: '6.1.1b', 
          title: 'General actions to address risks and opportunities', 
          requirement: 'Plan actions to address risks and opportunities', 
          documents: [
            'Required Document(s):',
            '- Risk Treatment Plan',
            '- Risk Action Tracking Log'
          ], 
          responsibility: 'Client' 
        },
        { 
          id: '6.1.1c', 
          title: 'General actions to address risks and opportunities', 
          requirement: 'Technical risk identification and monitoring', 
          documents: [
            'Required Document(s):',
            '- Technical Risk Assessment Results',
            '- Vulnerability Management Program'
          ], 
          responsibility: 'Kolega' 
        },
        { 
          id: '6.1.2a', 
          title: 'Information security risk assessment', 
          requirement: 'Define and apply risk assessment process', 
          documents: [
            'Required Document(s):',
            '- 04-ISMS Risk Assessment and Risk Treatment Process',
            '- Risk Management Policy'
          ], 
          responsibility: 'Client' 
        },
        { 
          id: '6.1.2b', 
          title: 'Information security risk assessment', 
          requirement: 'Conduct risk assessments', 
          documents: [
            'Required Document(s):',
            '- Risk Assessment Results',
            '- Risk Register'
          ], 
          responsibility: 'Client' 
        },
        { 
          id: '6.1.2c', 
          title: 'Information security risk assessment', 
          requirement: 'Technical risk assessment', 
          documents: [
            'Required Document(s):',
            '- Technical Vulnerability Assessment Reports',
            '- Penetration Test Reports',
            '- Technical Risk Register'
          ], 
          responsibility: 'Kolega' 
        },
        { 
          id: '6.1.3a', 
          title: 'Information security risk treatment', 
          requirement: 'Define and apply risk treatment process', 
          documents: [
            'Required Document(s):',
            '- 04-ISMS Risk Assessment and Risk Treatment Process'
          ], 
          responsibility: 'Client' 
        },
        { 
          id: '6.1.3b', 
          title: 'Information security risk treatment', 
          requirement: 'Document controls and produce Statement of Applicability', 
          documents: [
            'Required Document(s):',
            '- 11-ISMS Statement of Applicability (SoA)'
          ], 
          responsibility: 'Client' 
        },
        { 
          id: '6.1.3c', 
          title: 'Information security risk treatment', 
          requirement: 'Formulate risk treatment plan', 
          documents: [
            'Required Document(s):',
            '- Risk Treatment Plan',
            '- Risk Owner Approval Records'
          ], 
          responsibility: 'Client' 
        },
        { 
          id: '6.1.3d', 
          title: 'Information security risk treatment', 
          requirement: 'Technical risk remediation', 
          documents: [
            'Required Document(s):',
            '- Technical Control Implementation Plan',
            '- Remediation Status Reports'
          ], 
          responsibility: 'Kolega' 
        },
        { 
          id: '6.2a', 
          title: 'Information security objectives and planning to achieve them', 
          requirement: 'Establish information security objectives', 
          documents: [
            'Required Document(s):',
            '- 10-ISMS Information Security Objectives Plan'
          ], 
          responsibility: 'Client' 
        },
        { 
          id: '6.2b', 
          title: 'Information security objectives and planning to achieve them', 
          requirement: 'Plan to achieve objectives', 
          documents: [
            'Required Document(s):',
            '- Security Implementation Roadmap',
            '- Resource Allocation Plan'
          ], 
          responsibility: 'Client' 
        },
        { 
          id: '6.2c', 
          title: 'Information security objectives and planning to achieve them', 
          requirement: 'Technical security objectives', 
          documents: [
            'Required Document(s):',
            '- Technical Security Roadmap',
            '- Security Control Implementation Plan'
          ], 
          responsibility: 'Kolega' 
        }
      ]
    },
    {
      section: '7. Support',
      subsections: [
        { 
          id: '7.1', 
          title: 'Resources', 
          requirement: 'To be completed', 
          documents: [
            'To be completed'
          ], 
          responsibility: '' 
        }
      ]
    },
    {
      section: '8. Operation',
      subsections: [
        { 
          id: '8.1', 
          title: 'Operational planning and control', 
          requirement: 'To be completed', 
          documents: [
            'To be completed'
          ], 
          responsibility: '' 
        }
      ]
    },
    {
      section: '9. Performance evaluation',
      subsections: [
        { 
          id: '9.1', 
          title: 'Monitoring, measurement, analysis and evaluation', 
          requirement: 'To be completed', 
          documents: [
            'To be completed'
          ], 
          responsibility: '' 
        }
      ]
    },
    {
      section: '10. Improvement',
      subsections: [
        { 
          id: '10.1', 
          title: 'Nonconformity and corrective action', 
          requirement: 'To be completed', 
          documents: [
            'To be completed'
          ], 
          responsibility: '' 
        }
      ]
    }
  ];

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold mb-6">ISO 27001 Requirements Matrix</h1>
      <p className="mb-6">
        This matrix outlines the ISO 27001 requirements and responsibilities between Kolega and clients.
        Last updated: {new Date().toLocaleDateString()}
      </p>

      {requirementsData.map((section) => (
        <div key={section.section} className="mb-6 border rounded-lg overflow-hidden">
          <div 
            className="bg-gray-100 p-4 flex justify-between items-center cursor-pointer"
            onClick={() => toggleSection(section.section.split('.')[0])}
          >
            <h2 className="text-lg font-semibold">{section.section}</h2>
            <span>{expandedSections[section.section.split('.')[0]] ? '▼' : '▶'}</span>
          </div>
          
          {expandedSections[section.section.split('.')[0]] && (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/12">Subsection</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4">Requirement</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-3/5">Required Document(s)</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {section.subsections.map((subsection) => (
                    <tr key={subsection.id}>
                      <td className="px-4 py-4 text-sm font-medium text-gray-900">
                        <div className="font-bold">{subsection.id}</div>
                        <div className="text-xs mt-1">{subsection.title}</div>
                        {subsection.responsibility && (
                          <div className="mt-2">
                            <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              subsection.responsibility === 'Kolega' 
                                ? 'bg-blue-100 text-blue-800' 
                                : subsection.responsibility === 'Client'
                                ? 'bg-amber-100 text-amber-800'
                                : 'bg-purple-100 text-purple-800'
                            }`}>
                              {subsection.responsibility}
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-500">
                        {subsection.requirement}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-500">
                        {Array.isArray(subsection.documents) ? (
                          <div className="space-y-3">
                            {subsection.documents.map((doc, index) => {
                              if (doc.endsWith(':')) {
                                // Section headers
                                return (
                                  <div key={index} className="font-semibold text-gray-700 mt-3 border-b pb-1 flex justify-between items-center">
                                    <span>{doc}</span>
                                    {/* Small progress indicator */}
                                    <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                      <div className="bg-green-500 h-full rounded-full" style={{width: '60%'}}></div>
                                    </div>
                                  </div>
                                );
                              } else if (doc.startsWith('-')) {
                                // List items as card components
                                return (
                                  <div key={index} className="bg-gray-50 rounded-md p-2 border border-gray-100 hover:border-blue-200 transition-colors cursor-pointer">
                                    <div className="flex items-start">
                                      <span className="text-blue-500 mr-2 mt-0.5 flex-shrink-0">•</span>
                                      <span className="flex-grow">{doc.substring(2)}</span>
                                    </div>
                                  </div>
                                );
                              } else if (doc === '') {
                                // Empty line for spacing
                                return <div key={index} className="h-1"></div>;
                              } else {
                                // Regular text
                                return <div key={index} className="mb-1">{doc}</div>;
                              }
                            })}
                          </div>
                        ) : (
                          subsection.documents
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ))}

      <div className="mt-8 p-4 bg-gray-50 rounded-lg">
        <h3 className="font-semibold mb-2">Responsibility Legend:</h3>
        <div className="flex items-center space-x-6">
          <div className="flex items-center">
            <span className="w-4 h-4 inline-block rounded-full bg-blue-100 mr-2"></span>
            <span className="text-sm">Kolega Responsibility</span>
          </div>
          <div className="flex items-center">
            <span className="w-4 h-4 inline-block rounded-full bg-amber-100 mr-2"></span>
            <span className="text-sm">Client Responsibility</span>
          </div>
          <div className="flex items-center">
            <span className="w-4 h-4 inline-block rounded-full bg-purple-100 mr-2"></span>
            <span className="text-sm">Shared Responsibility</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ISO27001RequirementsMatrix;
